import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { FaCog } from 'react-icons/fa';
import adminSchemaApi from '../api/adminSchemaApi';
import {
  clampEntitlementsToPlan,
  DEFAULT_ENTITLEMENTS_CONFIG,
  normalizeEntitlements,
  normalizePlanConfig,
  prepareLicenseEntitlementsForSave,
} from './AdminPlanConfigConstants';
import EntitlementsForm from './EntitlementsForm';
import '../styles/admin-theme.css';

function deepMerge(base, overrides) {
  if (!overrides || typeof overrides !== 'object') return base ? JSON.parse(JSON.stringify(base)) : {};
  const out = base ? JSON.parse(JSON.stringify(base)) : {};
  Object.keys(overrides).forEach((k) => {
    if (overrides[k] != null && typeof overrides[k] === 'object' && !Array.isArray(overrides[k])) {
      out[k] = deepMerge(out[k] || {}, overrides[k]);
    } else {
      out[k] = overrides[k];
    }
  });
  return out;
}

function getSubscriptionEffectiveConfig(planConfig, subscription) {
  if (!planConfig) return planConfig;
  const subConfig = subscription?.config && typeof subscription.config === 'object' ? subscription.config : {};
  return deepMerge(JSON.parse(JSON.stringify(planConfig)), subConfig);
}

/**
 * Configure entitlements for a company_license_type (one row per subscription + license type).
 * Saves via updateCompanyLicenseTypeEntitlements; applies to all seats of that type.
 */
const LicenseTypeEntitlementsModal = ({
  show,
  onHide,
  companyId,
  licenseTypeRecord,
  subscription,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [subscriptionEffectiveConfig, setSubscriptionEffectiveConfig] = useState(null);
  const [standardObjects, setStandardObjects] = useState([]);
  const [standardTabs, setStandardTabs] = useState([]);
  const [entitlementsConfig, setEntitlementsConfig] = useState(() => JSON.parse(JSON.stringify(DEFAULT_ENTITLEMENTS_CONFIG)));

  useEffect(() => {
    if (!show || !subscription?.plan_id || !licenseTypeRecord?.license_type_id) return;
    setError('');
    setLoading(true);
    Promise.all([
      adminSchemaApi.fetchPlanById(subscription.plan_id),
      adminSchemaApi.fetchPlanLicenses(subscription.plan_id),
    ])
      .then(async ([plan, planLicenses]) => {
        const planCfg = normalizePlanConfig(plan?.config || {});
        const effectiveCfg = getSubscriptionEffectiveConfig(planCfg, subscription);
        setSubscriptionEffectiveConfig(effectiveCfg);
        const packages = effectiveCfg?.enabledPackages ?? planCfg?.enabledPackages ?? plan?.config?.enabledPackages ?? [];
        const [objects, tabs] = await Promise.all([
          adminSchemaApi.fetchStandardObjects(packages).catch(() => []),
          adminSchemaApi.fetchStandardTabs(packages).catch(() => []),
        ]);
        setStandardObjects(Array.isArray(objects) ? objects : []);
        setStandardTabs(Array.isArray(tabs) ? tabs : []);
        const pl = (planLicenses || []).find((p) => p.license_type_id === licenseTypeRecord.license_type_id);
        const base = pl ? normalizeEntitlements(pl.entitlements) : JSON.parse(JSON.stringify(DEFAULT_ENTITLEMENTS_CONFIG));
        const overrides = licenseTypeRecord.entitlements && typeof licenseTypeRecord.entitlements === 'object' ? licenseTypeRecord.entitlements : {};
        const merged = clampEntitlementsToPlan(deepMerge(base, overrides), effectiveCfg);
        setEntitlementsConfig(merged || JSON.parse(JSON.stringify(DEFAULT_ENTITLEMENTS_CONFIG)));
      })
      .catch(() => setError('Failed to load plan license entitlements'))
      .finally(() => setLoading(false));
  }, [show, subscription?.plan_id, subscription?.config, licenseTypeRecord?.license_type_id, licenseTypeRecord?.entitlements]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!companyId || !licenseTypeRecord?.id) return;
    setSaving(true);
    setError('');
    try {
      const payload = prepareLicenseEntitlementsForSave(
        clampEntitlementsToPlan(entitlementsConfig, subscriptionEffectiveConfig)
      );
      delete payload.searchView;
      await adminSchemaApi.updateCompanyLicenseTypeEntitlements(companyId, licenseTypeRecord.id, { entitlements: payload });
      onHide();
      if (typeof onSuccess === 'function') onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to save entitlements');
    } finally {
      setSaving(false);
    }
  };

  if (!licenseTypeRecord) return null;

  const displayName = licenseTypeRecord.license_type_name || licenseTypeRecord.license_type_code || 'License type';

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title className="admin-title">
          <FaCog className="me-2" />
          Configure entitlements — {displayName}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <p className="text-muted small mb-3">
            Configure entitlements for this license type on the subscription. This applies to all {licenseTypeRecord.quantity ?? 0} seat(s) of this type.
            Values are clamped to subscription config (plan + subscription overrides). Search Views are configured on the subscription override only, not per license type.
          </p>
          {loading ? (
            <p className="admin-subtitle">Loading…</p>
          ) : subscriptionEffectiveConfig ? (
            <>
              <h6 className="mt-2 mb-2">Entitlements (within subscription config limits)</h6>
              <EntitlementsForm
                entitlementsConfig={entitlementsConfig}
                onChange={setEntitlementsConfig}
                planConfig={subscriptionEffectiveConfig}
                disabled={saving}
                standardObjects={standardObjects}
                standardTabs={standardTabs}
                hideSearchViewEntitlements
              />
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button className="admin-primary-btn" type="submit" disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default LicenseTypeEntitlementsModal;
